import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { supabase } from '@/components/atomic-crm/providers/supabase/supabase';
import { assinar, definirEstado, lerEstado, loja } from './loja';

/**
 * O olhinho da pessoa — UM estado para todas as telas cobertas (Dashboard,
 * Vendas, Inadimplentes, Clientes, Comissões e Campanhas), salvo na conta
 * (`preferencias_usuario`, migration 244, chave única `valores_ocultos`):
 * vale em qualquer aparelho e não mexe na escolha de ninguém mais.
 *
 * ── ESCONDIDO ATÉ PROVAR O CONTRÁRIO ────────────────────────────────────────
 * O padrão de quem nunca mexeu é ESCONDIDO, e enquanto a preferência não
 * chegou as telas também ficam escondidas. Mostrar o valor real e esconder um
 * instante depois é justamente o que o olhinho existe para evitar (alguém
 * olhando por cima do ombro). Só revela quando o banco diz {"oculto": false}.
 *
 * ── UMA MEMÓRIA SÓ ──────────────────────────────────────────────────────────
 * O estado mora na `loja` (fora do React): cada tela coberta só assina. Abrir
 * outra tela não começa do zero — mostra o que já se sabe e confere o banco
 * de novo em segundo plano (é assim que a troca feita no celular aparece no
 * computador na próxima tela aberta).
 *
 * ── CLIQUE NÃO ESPERA O BANCO ───────────────────────────────────────────────
 * O clique troca na hora e grava em seguida (upsert). Se a gravação falhar, a
 * tela continua como a pessoa pediu — só não fica lembrado; o erro vai pro
 * console. E um clique feito ANTES da leitura terminar vale mais que a leitura:
 * ela não desfaz o que a pessoa acabou de escolher.
 *
 * As linhas antigas por tela ('valores_ocultos:dashboard' etc.) só existiram
 * em dado de teste do DEV e são ignoradas.
 */

export const CHAVE_OLHINHO = 'valores_ocultos';

// Tabela da 244 ainda fora de database.generated.ts: cliente sem tipo, como em useAvisosUsuario.
const sb = supabase as unknown as { from: (table: string) => any };

export interface EstadoOlhinho {
  /** O que a tela mostra agora. true durante a carga e sem preferência salva. */
  oculto: boolean;
  /** A preferência ainda não chegou do banco. */
  carregando: boolean;
  alternar: () => void;
}

const lerUsuario = async (): Promise<string | null> => {
  const { data } = await supabase.auth.getSession();
  return data?.session?.user?.id ?? null;
};

const ler = async () => {
  const cliquesNoInicio = loja.cliques;
  let uid: string | null = null;
  try {
    uid = await lerUsuario();
    if (uid !== loja.dono) {
      // Primeira leitura, ou outra pessoa entrou: esconde até saber a escolha DELA.
      loja.dono = uid;
      definirEstado({ oculto: true, carregando: true });
    }
    if (!uid) return;
    const { data, error } = await sb
      .from('preferencias_usuario')
      .select('valor')
      .eq('user_id', uid)
      .eq('chave', CHAVE_OLHINHO)
      .maybeSingle();
    if (error) throw error;
    if (loja.dono !== uid || loja.cliques !== cliquesNoInicio || loja.pendentes > 0) return;
    definirEstado({ oculto: data?.valor?.oculto !== false });
  } catch (erro) {
    // Sem tabela, sem rede: fica como está (escondido, na primeira vez).
    console.error('[olhinho] não consegui ler a preferência', erro);
  } finally {
    if (loja.dono === uid) definirEstado({ carregando: false });
  }
};

/** Confere o banco. Duas telas abrindo juntas dividem a mesma consulta. */
export const sincronizarValoresOcultos = (): Promise<void> => {
  if (!loja.leitura) {
    const leitura = ler().finally(() => {
      if (loja.leitura === leitura) loja.leitura = null;
    });
    loja.leitura = leitura;
  }
  return loja.leitura;
};

const gravar = async (oculto: boolean) => {
  loja.pendentes += 1;
  try {
    const uid = loja.dono ?? (await lerUsuario());
    if (!uid) return;
    const { error } = await sb.from('preferencias_usuario').upsert(
      {
        user_id: uid,
        chave: CHAVE_OLHINHO,
        valor: { oculto },
        atualizado_em: new Date().toISOString(),
      },
      { onConflict: 'user_id,chave' },
    );
    if (error) throw error;
  } catch (erro) {
    console.error('[olhinho] não consegui gravar a preferência', erro);
  } finally {
    loja.pendentes = Math.max(0, loja.pendentes - 1);
  }
};

export const alternarValoresOcultos = () => {
  loja.cliques += 1;
  const novo = !loja.estado.oculto;
  definirEstado({ oculto: novo });
  void gravar(novo);
};

export function useValoresOcultos(): EstadoOlhinho {
  const estado = useSyncExternalStore(assinar, lerEstado, lerEstado);

  useEffect(() => {
    void sincronizarValoresOcultos();
  }, []);

  const alternar = useCallback(() => alternarValoresOcultos(), []);

  return { oculto: estado.oculto, carregando: estado.carregando, alternar };
}
